import { useState, useMemo } from "react";
import { CheckCircle2, AlertTriangle, AlertOctagon } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { ReportFreshnessModal } from "@/components/report-freshness-modal";
import { useReportFreshness, type ReportType } from "@/hooks/use-report-freshness";

const CRITICAL_STALE_DAYS = 2;

interface ReportFreshnessTextProps {
  reportTypes: ReportType[];
  testIdPrefix?: string;
}

export function ReportFreshnessText({
  reportTypes,
  testIdPrefix = "freshness",
}: ReportFreshnessTextProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const { allPets, interaction, getStaleStatus, isLoading } = useReportFreshness();

  const status = useMemo(() => getStaleStatus(reportTypes), [getStaleStatus, reportTypes]);

  const isCriticallyStale = useMemo(() => {
    if (!reportTypes.includes("allPets")) return false;
    return allPets.staleLocations.some((loc) => loc.staleDays >= CRITICAL_STALE_DAYS);
  }, [allPets, reportTypes]);

  const isBothTypes = reportTypes.includes("allPets") && reportTypes.includes("interaction");

  if (isLoading) {
    return <Skeleton className="h-4 w-28" data-testid={`skeleton-${testIdPrefix}-text`} />;
  }

  if (status.staleCount === 0) {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-sm text-emerald-600 dark:text-emerald-500"
        data-testid={`text-${testIdPrefix}-fresh`}
      >
        <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
        Data is current
      </span>
    );
  }

  const isOutOfDate = isCriticallyStale;
  const Icon = isOutOfDate ? AlertOctagon : AlertTriangle;
  const textClass = isOutOfDate
    ? "text-red-600 dark:text-red-400"
    : "text-amber-600 dark:text-amber-500";
  const label = isOutOfDate ? "Data is out of date" : "Data is stale";

  return (
    <>
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        className={`inline-flex items-center gap-1.5 text-sm ${textClass} hover:underline focus-visible:outline-none focus-visible:underline`}
        data-testid={`text-${testIdPrefix}-stale`}
      >
        <Icon className="h-3.5 w-3.5 flex-shrink-0" />
        {label}
      </button>

      <ReportFreshnessModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        staleLocations={status.staleLocations}
        locationsWithGaps={status.locationsWithGaps}
        reportTypes={reportTypes}
        isCriticallyStale={isCriticallyStale}
        allLocations={isBothTypes ? undefined : (reportTypes.includes("allPets") ? allPets.locations : interaction.locations)}
        allPetsLocations={isBothTypes ? allPets.locations : undefined}
        interactionLocations={isBothTypes ? interaction.locations : undefined}
      />
    </>
  );
}
